import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MarketInsights = ({ insights, trendingItems }) => {
  const [activeInsight, setActiveInsight] = useState(0);

  const insightTypes = [
    { type: 'opportunity', icon: 'TrendingUp', color: 'text-success', bg: 'bg-success/10' },
    { type: 'warning', icon: 'AlertTriangle', color: 'text-warning', bg: 'bg-warning/10' },
    { type: 'info', icon: 'Info', color: 'text-primary', bg: 'bg-primary/10' },
    { type: 'alert', icon: 'AlertCircle', color: 'text-destructive', bg: 'bg-destructive/10' }
  ];

  const getInsightStyle = (type) => {
    const style = insightTypes?.find(s => s?.type === type);
    return style || insightTypes?.[2]; // default to info
  };

  return (
    <div className="space-y-6">
      {/* Market Insights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-card-foreground">Market Insights</h3>
          <button className="text-sm text-primary hover:text-primary/80 transition-colors duration-200">
            View All
          </button>
        </div>

        <div className="space-y-4">
          {insights?.map((insight, index) => {
            const style = getInsightStyle(insight?.type);
            return (
              <div
                key={insight?.id}
                className={`p-4 rounded-lg border transition-all duration-300 cursor-pointer premium-hover ${
                  activeInsight === index
                    ? `${style?.bg} border-current ${style?.color}`
                    : 'bg-muted/20 border-border hover:bg-muted/40'
                }`}
                onClick={() => setActiveInsight(index)}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${style?.bg}`}>
                    <Icon name={style?.icon} size={16} className={style?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-card-foreground mb-1">
                      {insight?.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      {insight?.description}
                    </p>
                    {insight?.action && (
                      <button className={`text-xs font-medium ${style?.color} hover:underline`}>
                        {insight?.action}
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {insight?.timeAgo}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Trending Items */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-card-foreground">Trending in Your Categories</h3>
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-sm text-success font-medium">Hot Market</span>
          </div>
        </div>

        <div className="space-y-4">
          {trendingItems?.map((item, index) => (
            <div key={item?.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg text-primary font-bold text-sm">
                #{index + 1}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-card-foreground truncate">
                      {item?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {item?.category} • {item?.condition}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-medium text-card-foreground">
                      ${item?.currentPrice?.toLocaleString()}
                    </div>
                    <div className={`text-xs flex items-center ${
                      item?.priceChange >= 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      <Icon 
                        name={item?.priceChange >= 0 ? "TrendingUp" : "TrendingDown"} 
                        size={12} 
                        className="mr-1" 
                      />
                      {item?.priceChange >= 0 ? '+' : ''}{item?.priceChange}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <button className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200">
            <Icon name="ExternalLink" size={16} />
            <span className="text-sm font-medium">View Market Analysis</span>
          </button>
        </div>
      </div>
      {/* Quick Market Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-lg font-bold text-card-foreground">+15.2%</div>
              <div className="text-xs text-muted-foreground">Market Growth</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-lg font-bold text-card-foreground">2.4k</div>
              <div className="text-xs text-muted-foreground">Active Auctions</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketInsights;